import { ref, type Ref } from "vue";
import {
  detectInAppBrowser,
  detectMobilePlatform,
  buildAndroidIntentUrl,
  buildIosExternalBrowserUrl,
  type InAppBrowserApp,
  type MobilePlatform,
} from "@/utils/inAppBrowser";

// Time to wait before assuming the auto-redirect attempt failed and the
// manual-instructions fallback should be shown.
const FALLBACK_DELAY_MS = 1500;

const app: Ref<InAppBrowserApp> = ref(null);
const platform: Ref<MobilePlatform> = ref(null);
const showFallback: Ref<boolean> = ref(false);

function attemptRedirect(currentUrl: string): void {
  if (platform.value === "android") {
    window.location.href = buildAndroidIntentUrl(currentUrl);

    return;
  }

  if (platform.value === "ios") {
    window.location.href = buildIosExternalBrowserUrl(currentUrl);
  }
}

function scheduleFallback(): void {
  window.setTimeout(() => {
    // If the tab is still visible, the auto-redirect attempt didn't
    // navigate the user away, so fall back to manual instructions.
    if (document.visibilityState === "visible") {
      showFallback.value = true;
    }
  }, FALLBACK_DELAY_MS);
}

/**
 * Detects Instagram/TikTok's in-app browser (IAB) and tries to escape it:
 * an automatic redirect (Android intent:// / iOS x-safari- scheme) backed
 * by a manual-instructions fallback, since IAB vendors can block either
 * trick at any time.
 */
function detect(userAgent: string = navigator.userAgent): void {
  app.value = detectInAppBrowser(userAgent);

  if (!app.value) return;

  platform.value = detectMobilePlatform(userAgent);
  attemptRedirect(window.location.href);
  scheduleFallback();
}

async function copyCurrentUrl(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.href);

    return true;
  } catch {
    return false;
  }
}

function dismissFallback(): void {
  showFallback.value = false;
}

export function useInAppBrowser() {
  return {
    app,
    platform,
    showFallback,
    detect,
    copyCurrentUrl,
    dismissFallback,
  };
}
